import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy } from 'firebase/firestore';

// Estilos mejorados
const styles = {
    // Paleta de colores
    primaryColor: '#3498db', // Un azul vibrante
    accentColor: '#2ecc71', // Un verde para acciones positivas
    dangerColor: '#e74c3c', // Un rojo para acciones negativas
    textColor: '#2c3e50', // Texto oscuro
    lightGray: '#ecf0f1', // Fondo claro
    mediumGray: '#bdc3c7', // Bordes y separadores
    darkGray: '#7f8c8d', // Texto secundario

    container: { 
        paddingTop: '2rem', 
        paddingLeft: '1rem', 
        paddingRight: '1rem', 
        paddingBottom: '1rem', 
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        color: '#2c3e50' // Usar textColor directamente aquí
    },
    header: { 
        fontSize: '1.8rem', 
        fontWeight: '700', 
        color: '#2c3e50', // Usar textColor directamente aquí
        marginBottom: '1rem', 
        paddingBottom: '0.5rem', 
        borderBottom: `2px solid #bdc3c7` // Usar mediumGray directamente aquí
    },
    th: { 
        border: `1px solid #bdc3c7`, // Usar mediumGray directamente aquí
        padding: '8px 4px', 
        backgroundColor: '#2c3e50', // Usar textColor directamente aquí
        color: 'white', 
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.8rem' // Reducir tamaño de fuente para encabezados
    },
    td: { 
        border: `1px solid #bdc3c7`, // Usar mediumGray directamente aquí
        padding: '6px 4px', 
        color: '#2c3e50', // Usar textColor directamente aquí
        fontSize: '0.8rem' // Reducir tamaño de fuente para celdas
    },
    button: { 
        padding: '8px 12px', 
        cursor: 'pointer', 
        color: 'white', 
        border: 'none', 
        borderRadius: '5px', 
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease'
    },
    // Estilos para los badges de estado
    statusBadge: {
        padding: '5px 10px',
        borderRadius: '15px',
        fontWeight: 'bold',
        color: 'white',
        display: 'inline-block',
        minWidth: '80px',
        textAlign: 'center'
    },
    statusApproved: { backgroundColor: '#2ecc71' }, // Usar accentColor directamente aquí
    statusPending: { backgroundColor: '#f39c12' }, // Naranja más oscuro
    statusDeclined: { backgroundColor: '#e74c3c' }, // Usar dangerColor directamente aquí
    actionButtonConfirm: { backgroundColor: '#2ecc71', '&:hover': { backgroundColor: '#27ae60' } }, // Usar accentColor directamente aquí
    actionButtonReject: { backgroundColor: '#e74c3c', '&:hover': { backgroundColor: '#c0392b' } }, // Usar dangerColor directamente aquí
};

const AdminPromotionBookings = () => {
  const { promotionId } = useParams();
  const [bookings, setBookings] = useState([]);
  const [promotion, setPromotion] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const navigate = useNavigate();

  useEffect(() => {
    // Setup Firestore listener
    let q = collection(db, "promotionBookings");

    if (promotionId) {
      q = query(q, where("promotionId", "==", promotionId));
    }
    if (filterStatus !== 'all') {
      q = query(q, where("status", "==", filterStatus));
    }
    q = query(q, orderBy(sortBy, sortOrder));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedBookings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookings(fetchedBookings);

      // Set promotion details from the first booking if available, or fetch if needed
      if (fetchedBookings.length > 0 && !promotion) {
        setPromotion({ id: promotionId, nombre: fetchedBookings[0].promotionTitle });
      } else if (!promotionId) {
        setPromotion({ id: 'all', nombre: 'Todas las Promociones' });
      }

    }, (error) => {
      console.error("Error fetching promotion bookings: ", error);
      alert("Error al cargar las reservas: " + error.message);
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, [promotionId, filterStatus, sortBy, sortOrder, promotion]);

  const handleConfirmPayment = async (bookingId) => {
    try {
      const bookingRef = doc(db, "promotionBookings", bookingId);
      await updateDoc(bookingRef, {
        status: 'approved',
        updatedAt: new Date().toISOString(),
      });
      alert('¡Pago confirmado en Firestore!');
    } catch (error) {
      console.error("Error confirming payment: ", error);
      alert("Error al confirmar el pago: " + error.message);
    }
  };

  const handleRejectPayment = async (bookingId) => {
    if (!window.confirm("¿Estás seguro de que quieres RECHAZAR esta reserva? Esta acción no se puede deshacer fácilmente.")) {
      return;
    }
    try {
      const bookingRef = doc(db, "promotionBookings", bookingId);
      await updateDoc(bookingRef, {
        status: 'declined',
        updatedAt: new Date().toISOString(),
      });
      alert('¡Reserva rechazada en Firestore!');
    } catch (error) {
      console.error("Error rejecting payment: ", error);
      alert("Error al rechazar el pago: " + error.message);
    }
  };

  const getStatusStyle = (status) => {
    const baseStyle = styles.statusBadge;
    switch (status) {
      case 'approved': return { ...baseStyle, ...styles.statusApproved };
      case 'pending': return { ...baseStyle, ...styles.statusPending };
      case 'declined': return { ...baseStyle, ...styles.statusDeclined };
      default: return baseStyle;
    }
  };

  if (!promotion) {
    return <div style={styles.container}>Cargando detalles de la promoción...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Reservas para: {promotion.nombre}</h1>


      <div style={styles.controls}>
        <label>Filtrar por estado:</label>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">Todos</option>
          <option value="approved">Aprobado</option>
          <option value="pending">Pendiente</option>
          <option value="declined">Rechazado</option>
        </select>

        <label>Ordenar por:</label>
        <select style={styles.select} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="createdAt">Fecha de Creación</option>
          <option value="customerName">Nombre del Cliente</option>
          <option value="price">Precio</option>
        </select>

        <select style={styles.select} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
          <option value="desc">Descendente</option>
          <option value="asc">Ascendente</option>
        </select>
      </div>

      {bookings.length === 0 ? (
        <p>No hay reservas para esta promoción.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.bookingTable}>
            <thead>
              <tr>
                <th style={styles.th}>ID Reserva</th>
                <th style={styles.th}>Cliente</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Teléfono</th>
                <th style={styles.th}>Promoción / Opción</th>
                <th style={styles.th}>Fecha de Evento</th>
                <th style={styles.th}>Cupos</th>
                <th style={styles.th}>Precio</th>
                <th style={styles.th}>Método Pago</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Fecha de Creación</th>
                <th style={styles.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(booking => (
                <tr key={booking.id}>
                  <td style={styles.td}>{booking.id}</td>
                  <td style={styles.td}>{booking.customerData ? booking.customerData.fullName : 'N/A'}</td>
                  <td style={styles.td}>{booking.customerData ? booking.customerData.email : 'N/A'}</td>
                  <td style={styles.td}>{booking.customerData ? booking.customerData.phoneNumber : 'N/A'}</td>
                  <td style={styles.td}>{booking.promotionTitle} / {booking.optionName}</td>
                  <td style={styles.td}>{booking.selectedDate || 'N/A'}</td>
                  <td style={styles.td}>{booking.quantity}</td>
                  <td style={styles.td}>{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(booking.price)}</td>
                  <td style={styles.td}>{booking.paymentMethod}</td>
                  <td style={styles.td}>
                    <span style={getStatusStyle(booking.status)}>
                      {booking.status ? String(booking.status).toUpperCase() : 'N/A'}
                    </span>
                  </td>
                  <td style={styles.td}>{new Date(booking.createdAt && typeof booking.createdAt.toDate === 'function' ? booking.createdAt.toDate() : booking.createdAt).toLocaleString()}</td>
                  <td style={styles.td}>
                    {booking.paymentMethod === 'bankTransfer' && booking.status === 'pending_transfer' ? (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button style={{ ...styles.button, backgroundColor: styles.accentColor }} onClick={() => handleConfirmPayment(booking.id)}>
                          Confirmar
                        </button>
                        <button style={{ ...styles.button, backgroundColor: styles.dangerColor }} onClick={() => handleRejectPayment(booking.id)}>
                          Rechazar
                        </button>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPromotionBookings;
